/**
 * DatabaseObjectSearch
 *
 * Renders searchable database objects (tables, views, procedures, functions)
 * from preloaded data in the command palette. Selecting an object triggers
 * the drill-down page to show available actions.
 *
 * Supports a `mode` prop for split rendering:
 *  - 'promoted'  → only objects whose name starts with `search` (max 5)
 *  - 'remaining' → everything else (excludes promoted items)
 *  - undefined   → all objects (default, backward-compatible)
 */

import { Table2, Eye, Cog, FunctionSquare, Package } from 'lucide-react';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { caseFoldFor } from '../../connections/utils/databaseNameCase';
import { isSystemDatabase } from '../../explorer/models/nodeFactories';
import type { DatabaseObject, DatabaseObjectType } from '../../database-objects/models/databaseObjectTypes';
import type { SchemaObjectType } from '../../shared/object-actions/types';

/** Max number of objects to show for performance */
const MAX_OBJECTS = 500;
/** Max promoted items to show before commands */
const MAX_PROMOTED = 5;

/** Object types we search for */
const SEARCHABLE_TYPES: DatabaseObjectType[] = ['table', 'view', 'stored-procedure', 'function', 'package'];

/** Map DatabaseObjectType to display info */
const objectTypeConfig: Record<string, { label: string; icon: typeof Table2; schemaType: SchemaObjectType }> = {
  table: { label: 'Table', icon: Table2, schemaType: 'table' },
  view: { label: 'View', icon: Eye, schemaType: 'view' },
  'stored-procedure': { label: 'Procedure', icon: Cog, schemaType: 'procedure' },
  function: { label: 'Function', icon: FunctionSquare, schemaType: 'function' },
  package: { label: 'Package', icon: Package, schemaType: 'package' },
};

export type SelectedObject = {
  object: DatabaseObject;
  database: string;
  schemaObjectType: SchemaObjectType;
};

type DatabaseObjectSearchProps = {
  onSelectObject: (selected: SelectedObject) => void;
  /** Current search term — used for promoted/remaining split */
  search?: string;
  /** 'promoted' = only strong name matches; 'remaining' = everything else */
  mode?: 'promoted' | 'remaining';
};

/** Check if an object name is a strong match for the search term */
const isStrongMatch = (objName: string, search: string): boolean =>
  objName.toLowerCase().startsWith(search.toLowerCase());

export function DatabaseObjectSearch({ onSelectObject, search, mode }: DatabaseObjectSearchProps) {
  const preloadedData = useDatabaseStore((s) => s.preloadedData);
  const activeConnections = useConnectionStore((s) => s.activeConnections);

  // Only show when there are active connections
  const connectedIds = activeConnections
    .filter((c) => c.status === 'connected')
    .map((c) => c.id);

  if (connectedIds.length === 0) return null;

  // Flatten all objects across connections and databases, capped at MAX_OBJECTS
  const allObjects: { obj: DatabaseObject; database: string }[] = [];
  for (const connId of connectedIds) {
    const connData = preloadedData[connId];
    if (!connData?.objects) continue;
    const activeConn = activeConnections.find((c) => c.id === connId);
    const isOracle = activeConn?.connection?.engine === 'oracle';
    // The configured name and the listed one can differ in case on mssql/mysql
    // (`Master` vs `master`), so compare them the way the engine does.
    const fold = caseFoldFor(activeConn?.connection?.engine);
    const configured = activeConn?.connection?.database;
    const isConfigured = (database: string) =>
      configured !== undefined && fold(database) === fold(configured);
    for (const [database, objects] of Object.entries(connData.objects)) {
      // System databases (e.g. MSSQL `master`, PostgreSQL `postgres`) are
      // preloaded when they are the connection's home database, but their
      // objects shouldn't clutter object search — unless the user explicitly
      // configured that system database as the connection's own database.
      if (isSystemDatabase(database) && !isConfigured(database)) continue;
      for (const obj of objects) {
        if (!SEARCHABLE_TYPES.includes(obj.type)) continue;
        if (obj.type === 'package' && !isOracle) continue;
        allObjects.push({ obj, database });
        if (allObjects.length >= MAX_OBJECTS) break;
      }
      if (allObjects.length >= MAX_OBJECTS) break;
    }
    if (allObjects.length >= MAX_OBJECTS) break;
  }

  if (allObjects.length === 0) return null;

  // Apply mode-based filtering
  let filteredObjects = allObjects;
  if (mode && search) {
    if (mode === 'promoted') {
      filteredObjects = allObjects
        .filter(({ obj }) => isStrongMatch(obj.name, search))
        .slice(0, MAX_PROMOTED);
    } else {
      // 'remaining' — exclude items that would be promoted
      const promotedIds = new Set(
        allObjects
          .filter(({ obj }) => isStrongMatch(obj.name, search))
          .slice(0, MAX_PROMOTED)
          .map(({ obj, database }) => `${obj.connectionId}-${database}-${obj.id}`)
      );
      filteredObjects = allObjects.filter(
        ({ obj, database }) => !promotedIds.has(`${obj.connectionId}-${database}-${obj.id}`)
      );
    }
  } else if (mode === 'promoted') {
    // No search term → nothing to promote
    return null;
  }

  if (filteredObjects.length === 0) return null;

  // Group by object type for display
  const grouped = new Map<DatabaseObjectType, typeof filteredObjects>();
  for (const item of filteredObjects) {
    const list = grouped.get(item.obj.type) ?? [];
    list.push(item);
    grouped.set(item.obj.type, list);
  }

  return (
    <>
      {Array.from(grouped.entries()).map(([type, items]) => {
        const config = objectTypeConfig[type];
        if (!config) return null;
        const Icon = config.icon;

        return (
          <CommandGroup key={type} heading={`Database Objects: ${config.label}s`}>
            {items.map(({ obj, database }) => (
              <CommandItem
                key={`${obj.connectionId}-${database}-${obj.id}`}
                value={`${obj.schema}.${obj.name} ${database} ${config.label}`}
                keywords={[obj.name, obj.schema, database, config.label]}
                onSelect={() => onSelectObject({
                  object: obj,
                  database,
                  schemaObjectType: config.schemaType,
                })}
              >
                <Icon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                <span className="flex-1 truncate">
                  {obj.schema}.{obj.name}
                </span>
                <span className="ml-2 text-xs text-muted-foreground truncate">
                  {database}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        );
      })}
    </>
  );
}
